import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Heart, Loader2, Shirt } from 'lucide-react';
import { getBrands, setBrandAllowed, VaultIntent, vaultErrorMessage } from '../../services/vaultService';
import { showToast } from '../ui/Toast';
import IntentSwitch from '../../pages/vault/components/IntentSwitch';
import VaultSheet from '../../pages/vault/components/VaultSheet';

interface SaveSheetProps {
  open: boolean;
  onClose: () => void;
  productName: string;
  imageUrl?: string;
  brandName?: string;
  brandId?: string;
  saved: boolean;
  intent: VaultIntent | null;
  // Which choice is being saved right now (spinner on that button).
  saving: VaultIntent | null;
  signedIn: boolean;
  onChoose: (intent: VaultIntent) => void;
  onRemove: () => void;
  onViewVault: () => void;
}

const CHOICES: { intent: VaultIntent; label: string; hint: string; icon: React.ReactNode }[] = [
  { intent: 'own', label: 'I own it', hint: "It's in my wardrobe", icon: <Shirt size={22} /> },
  { intent: 'want', label: 'Wishlist', hint: 'I want it', icon: <Heart size={22} /> },
];

// The product page's Save: choosing "I own it" or "Wishlist" saves the piece
// (#252: no silent default). Once saved, the same sheet changes the choice,
// lets this brand send news (off until switched on), or removes the piece.
const SaveSheet: React.FC<SaveSheetProps> = ({
  open,
  onClose,
  productName,
  imageUrl,
  brandName,
  brandId,
  saved,
  intent,
  saving,
  signedIn,
  onChoose,
  onRemove,
  onViewVault,
}) => {
  const [brandAllowed, setBrandAllowedState] = useState<boolean | null>(null);

  useEffect(() => {
    if (!open || !saved || !signedIn || !brandId) return;
    let cancelled = false;
    getBrands()
      .then(list => !cancelled && setBrandAllowedState(list.find(b => b.brandId === brandId)?.allowed ?? false))
      .catch(() => !cancelled && setBrandAllowedState(null));
    return () => {
      cancelled = true;
    };
  }, [open, saved, signedIn, brandId]);

  const toggleBrand = async () => {
    if (!brandId || brandAllowed === null) return;
    const next = !brandAllowed;
    setBrandAllowedState(next);
    try {
      await setBrandAllowed(brandId, next, 'save');
    } catch (err) {
      setBrandAllowedState(!next);
      showToast.error(vaultErrorMessage(err, 'Could not save that choice. Try again.'));
    }
  };

  return (
    <VaultSheet open={open} onClose={onClose} maxWidth="sm:max-w-sm" label={saved ? 'In your vault' : 'Save to your vault'} zIndex={72}>
      <AnimatePresence mode="wait" initial={false}>
        {!saved ? (
          <motion.div key="choose" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }}>
            <div className="flex items-center gap-3">
              {imageUrl && (
                <img src={imageUrl} alt="" className="h-14 w-14 shrink-0 rounded-[8px] object-cover" />
              )}
              <div className="min-w-0">
                <h3 className="v-display text-[21px] font-semibold leading-tight">Save to your vault</h3>
                <p className="truncate text-[14px] text-[var(--v-text-2)]">{productName}</p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {CHOICES.map(c => (
                <button
                  key={c.intent}
                  type="button"
                  onClick={() => onChoose(c.intent)}
                  disabled={saving !== null}
                  className="v-choice flex h-28 flex-col items-start justify-between p-4 text-left disabled:opacity-60"
                >
                  <span className="text-[var(--v-accent)]">
                    {saving === c.intent ? <Loader2 size={22} className="animate-spin" /> : c.icon}
                  </span>
                  <span>
                    <span className="block text-[16px] font-semibold">{c.label}</span>
                    <span className="block text-[12.5px] text-[var(--v-text-3)]">{c.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div key="saved" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }}>
            <div className="flex items-center gap-3">
              <motion.span
                className="grid h-12 w-12 shrink-0 place-items-center rounded-full text-white"
                style={{ background: 'var(--v-mark)' }}
                initial={{ scale: 0.4, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 460, damping: 16 }}
              >
                <Check size={24} strokeWidth={2.6} />
              </motion.span>
              <div className="min-w-0">
                <h3 className="v-display text-[21px] font-semibold leading-tight">In your vault</h3>
                <p className="truncate text-[14px] text-[var(--v-text-2)]">{productName}</p>
              </div>
            </div>

            <IntentSwitch className="mt-5" value={intent ?? 'own'} onChange={onChoose} disabled={saving !== null} />

            {brandId && brandName && signedIn && (
              <div className="mt-3 flex items-center gap-3 rounded-2xl px-4 py-3" style={{ background: 'var(--v-glass)' }}>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-medium">Let {brandName} send me news</span>
                  <span className="block text-[12.5px] text-[var(--v-text-3)]">They never see who you are.</span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!brandAllowed}
                  aria-label={`Let ${brandName} send me news`}
                  disabled={brandAllowed === null}
                  onClick={toggleBrand}
                  className="v-switch"
                />
              </div>
            )}

            <div className="mt-5 space-y-3">
              <button type="button" onClick={onViewVault} className="v-btn v-btn-primary w-full">
                Open my vault
              </button>
              <button type="button" onClick={onRemove} className="v-btn v-btn-danger-soft w-full">
                Remove from vault
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </VaultSheet>
  );
};

export default SaveSheet;
